//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import angular from 'angular';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { Modal } from '@components/molecules/modal/Modal';
import { galleriesCreationActions } from '@galleries/actions/galleriesCreationActions';
import { noGalleriesModalActions } from '@galleries/actions/noGalleriesModalActions';
import getGalleriesClientType, {
  GalleriesClientType,
} from '@galleries/selectors/getGalleriesClientType';
import NoGalleriesIllustration from '@images/no-galleries-created-illustration.svg';
import makeAngularReactController from '@react-bridge/angular-react-widget';
import { navigateToState } from '@react-bridge/wrappers/navigation';
import { getBulkActionsActions } from '@react-bridge/wrappers/ng-service-provider';

export const NoGalleriesModal = () => {
  const dispatch = useDispatch();

  const isOpen = useSelector(
    (state) => state.galleries.noGalleriesModal.isOpen,
  );

  const isInstaShopClient = useSelector(
    (state) => getGalleriesClientType(state) === GalleriesClientType.instashop,
  );

  const bulkActionsActions = getBulkActionsActions();

  const handleAnchorWidgetClick = () => {
    void navigateToState(isInstaShopClient ? 'instashop' : 'galleries');
    dispatch(noGalleriesModalActions.closeModal());
    dispatch(bulkActionsActions.closeConfirmationModal());
    dispatch(bulkActionsActions.clearSelectedItems());
    dispatch(galleriesCreationActions.openCreateGalleryModal());
  };

  return (
    <Modal
      closeModal={() => dispatch(noGalleriesModalActions.closeModal())}
      contents={
        <StyledModalBody>
          <ImageContainer>
            <StyledImage
              alt='No galleries illustration'
              src={NoGalleriesIllustration}
            />
          </ImageContainer>
          <CenteredTextContainer>
            {isInstaShopClient
              ? `Looks like you haven't created an InstaShop yet.`
              : `Looks like you haven't created any Galleries yet.`}
          </CenteredTextContainer>
          <CenteredTextContainer>
            You'll need to create{' '}
            {isInstaShopClient ? 'an InstaShop ' : 'a Gallery '}
            first to distribute this media.
          </CenteredTextContainer>
        </StyledModalBody>
      }
      isModalOpen={isOpen}
      primaryButtonInfo={{
        onClick: handleAnchorWidgetClick,
        buttonContent: isInstaShopClient
          ? 'Create InstaShop'
          : 'Create Gallery',
      }}
      shouldIncludeSecondaryButton={false}
      title={isInstaShopClient ? 'Create an InstaShop' : 'Create a Gallery'}
      width='wide'
    />
  );
};

const StyledModalBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const StyledImage = styled.img`
  width: 100%;
  height: 100%;
`;

const ImageContainer = styled.div(
  ({ theme }) => `
  margin-bottom: ${theme.spacings[4]};
`,
);

const CenteredTextContainer = styled.div`
  text-align: center;
`;

angular.module('curalate').component('noGalleriesModal', {
  controller: makeAngularReactController(<NoGalleriesModal />),
});
